import React, { useRef, useState, useEffect, useCallback } from 'react'

interface Props<T> {
  className?: string
  valueClassName?: string
  dropdownClassName?: string
  dropdownStyle?: React.CSSProperties
  value: T
  options: readonly T[]
  getOptionLabel?: (option: T) => string
  onChange: (option: T) => void
  disableScroll?: boolean
}

export function ScrollableSelect<T>({
  className = '',
  valueClassName = '',
  dropdownClassName = '',
  dropdownStyle = {},
  value,
  onChange,
  options,
  getOptionLabel = String,
  disableScroll = false,
}: Props<T>) {
  const ref = useRef<HTMLDivElement>()
  const [isOpen, setIsOpen] = useState(false)

  const handleOutsideClick = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const realTarget = event.composedPath()[0] as Node // Works also in ShadowDOM, as opposed to event.target
      if (ref.current.contains(realTarget)) {
        return
      }
      setIsOpen(false)
    },
    [setIsOpen]
  )

  useEffect(() => {
    if (isOpen) {
      document.body.addEventListener('mouseup', handleOutsideClick, {
        passive: true,
      })
      document.body.addEventListener('touchend', handleOutsideClick, {
        passive: true,
      })
    } else {
      document.body.removeEventListener('mouseup', handleOutsideClick)
      document.body.removeEventListener('touchend', handleOutsideClick)
    }
  }, [isOpen])

  const handleClick = () => setIsOpen((open) => !open)

  return (
    <div className={`${className} relative`} ref={ref}>
      <div className={`${valueClassName}`} onClick={handleClick}>
        {getOptionLabel(value)}
        <Arrow />
      </div>
      {isOpen && (
        <div
          className={`
            absolute bg-white vw-100 overflow-hidden br2 z-3 mr3 mt2 
            ${disableScroll ? '' : 'h5'} ${dropdownClassName}`}
          style={{
            width: 'calc(100vw - 18px)',
            maxWidth: 320,
            boxShadow: '0px 1px 9px 1px rgba(0, 0, 0, 0.15)',
            ...dropdownStyle,
          }}
        >
          <div className={`w-100 h-100 ${disableScroll ? '' : 'overflow-y-scroll'}`}>
            {options.map((option) => (
              <div
                key={getOptionLabel(option)}
                className={`pointer w5 hover-bg-gray100 pa2 w-100`}
                onClick={() => {
                  handleClick()
                  onChange(option)
                }}
              >
                {getOptionLabel(option)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const Arrow = () => (
  <svg width="13" height="8" viewBox="0 0 13 8" fill="none" style={{ paddingLeft: 8 }}>
    <path d="M1 1L6.5 6L12 1" stroke="currentColor" strokeWidth="2" />
  </svg>
)
